import {
    Button,
    ContactModel,
    ContactsFactory,
    DataTable,
    DialogHandler,
    Dropdown,
    EmptyContent,
    ETaskType,
    fetchRoute,
    FullBar,
    GenericWrapperContext,
    Icon,
    ListsFactory,
    LoadingContainer,
    Search,
    SuppressedEmailsFactory,
    TDataTableColumnsData,
    TDataTableRowData,
    TGenericListReturn,
    TNumStr,
    TOrder
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import PromisePool from "@supercharge/promise-pool"
import { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { showTaskDialog } from "../../../components/dialogs/export"
import { EPlanLimitsFeatures, showPlanLimitsDialog } from "../../../utils/limits_features"
import { ERoutes } from "../../../utils/url"
import { ContactActionBar } from "../components/contactActionBar"
import { getListingColsData, getListingRows } from "../utils"

export default function ContactsListing() {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const { listId } = useParams()
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)

    const [loading, setLoading] = useState(false)
    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(50)
    const [order, setOrder] = useState<TOrder>("desc")
    const [orderBy, setOrderBy] = useState<string>("subscribed_on")

    const [contacts, setContacts] = useState<TGenericListReturn<ContactModel>>()
    const [selectedContacts, setSelectedContacts] = useState<TNumStr[]>()

    const [selectAllPages, setSelectAllPages] = useState(false)

    const currentListId = searchParams.get("listId") || listId
    const currentSegmentId = searchParams.get("segmentId")

    useEffect(() => {
        if (!currentListId) return
        fetchContacts()
    }, [page, perPage, order, orderBy, search])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(fetchFirstList, [])

    function fetchFirstList() {
        if (currentListId) return
        setLoading(true)
        ListsFactory.list({
            page: 1,
            perPage: 1,
            sort: "created_on",
            withCount: true
        }).then((res) => {
            window.location.replace("#" + fetchRoute({ route: ERoutes.listContacts, params: { listId: res.data[0].id.toString() } }))
        }).catch(() => {
            window.location.replace("#" + fetchRoute({ route: ERoutes.lists }))
        })
    }

    function fetchContacts() {
        if (!currentListId) return
        setLoading(true)
        ContactsFactory.list({
            listId: Number(listId),
            page,
            perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            withCount: true
        }).then((res) => {
            setContacts(res)
        }).finally(() => {
            setLoading(false)
        })
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".contactsSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
        setSelectedContacts([])
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
        setSelectedContacts([])
    }

    function handlePaginationChange(cPage: number) {
        setSelectedContacts([])
        setPage(cPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleExport() {
        if (showPlanLimitsDialog({
            name: EPlanLimitsFeatures.use_contact_export,
            account: genericWrapper.account,
            user: genericWrapper.user,
            partnerBrand: genericWrapper.partnerBrand
        })) {
            return
        }

        showTaskDialog({
            taskType: ETaskType.contacts_export,
            context: {
                listId: Number(currentListId),
                segmentId: Number(currentSegmentId)
            }
        })
    }

    function handleUnsubscribeContact(contact: ContactModel) {
        contact.unsubscribe().then(() => {
            fetchContacts()
        }).catch((e) => {
            showGenericError()
        })
    }

    function handleUnsubscribeContacts() {
        const contactsLists = contacts?.data?.filter((contact: ContactModel) => selectedContacts?.includes(contact.id))
        return PromisePool.withConcurrency(10)
            .for(contactsLists || [])
            .process(async (contact: ContactModel, index, pool) => {
                return contact.unsubscribe()
            })
            .then(() => {
                fetchContacts()
            })
    }

    function handleRowSelect(event: React.MouseEvent<unknown> | React.ChangeEvent<HTMLInputElement>, id?: TNumStr, ids?: TNumStr[]) {
        setSelectedContacts(ids)
    }

    function handleSuppressContact(contact: ContactModel) {
        SuppressedEmailsFactory.add({
            email: contact.email
        })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("suppression-list-suppress-email-success", { email: contact.email })
                })
            })
            .catch((e) => {
                showGenericError()
            })
    }

    function handleSuppressContacts() {
        const contactsLists = contacts?.data?.filter((contact: ContactModel) => selectedContacts?.includes(contact.id))
        return PromisePool.withConcurrency(10)
            .for(contactsLists || [])
            .process(async (contact: ContactModel, index, pool) => {
                return SuppressedEmailsFactory.add({
                    email: contact.email
                })
            })
            .then(() => {
                DialogHandler.show({
                    isExpress: true,
                    type: "success",
                    title: t("suppression-list-suppress-email-success")
                })
                fetchContacts()
            })
    }

    function handleDeleteContact(contact: ContactModel) {
        showDeleteDialog({
            name: contact.email,
            resourceKey: "general-contact_one"
        })
            .then(({ btnRef }) => {
                btnRef.animate()
                return contact.delete()
            })
            .then(() => {
                DialogHandler.hide()
                fetchContacts()
            })
            .catch((e) => {
                if (e) {
                    showGenericError()
                }
            })
    }

    function handleDeleteContacts() {
        showDeleteDialog({
            name: selectedContacts?.length + " " + t("general-contact", { count: selectedContacts?.length }),
            resourceKey: "general-contact_other"
        })
            .then(({ btnRef }) => {
                btnRef.animate()
                const contactsLists = contacts?.data?.filter((contact: ContactModel) => selectedContacts?.includes(contact.id))
                return PromisePool.withConcurrency(10)
                    .for(contactsLists || [])
                    .process(async (contact: ContactModel, index, pool) => {
                        return contact.delete()
                    })
            })
            .then(() => {
                DialogHandler.hide()
                setSelectedContacts([])
                fetchContacts()
            })
    }

    function showGenericError() {
        DialogHandler.show({
            isExpress: true,
            type: "error",
            title: t("general-error-action")
        })
    }

    function handlePageSelection() {
        if (!selectAllPages && selectedContacts?.length !== contacts?.data?.length) {
            setSelectedContacts(contacts?.data?.map((contact: ContactModel) => contact.id) || [])
        } else {
            setSelectAllPages(!selectAllPages)
        }
    }

    function handleToggleInterestManager() {
        // TODO: Implement handleToggleInterestManager
    }

    function handleToggleTagsManager() {
        // TODO: Implement handleToggleTagsManager
    }

    function handleCloseActionRow() {
        setSelectedContacts([])
        setSelectAllPages(false)
    }

    function handleCreateContact() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.addContacts })
    }

    function handleViewExports() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.export })
    }

    function handleImportContacts() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.importContacts }) + "?listId=" + currentListId
    }

    function handleOpenContact(contact: ContactModel) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.listContactDetails, params: { listId: currentListId, contactId: contact.id.toString() } })
    }

    const colsData: TDataTableColumnsData[] = useMemo(getListingColsData(), [])
    const rows: TDataTableRowData[] = useMemo(() => contacts?.data.map((contact: ContactModel) => {
        return getListingRows({
            contact,
            handleOpen: handleOpenContact,
            handleUnsubscribe: handleUnsubscribeContact,
            handleAddToSuppressionList: handleSuppressContact,
            handleDelete: handleDeleteContact
        })
    }) || [], [contacts?.data, selectedContacts, page, perPage, order, orderBy])

    return <Stack className="contactsListingContainer" gap={8}>
        <FullBar
            className={loading ? "loading" : ""}
            right={[
                <Button
                    key="create-contact"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreateContact}
                    iconOnly
                    disabled={loading}
                ><Icon name="Add18" /></Button>,
                <Button
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>,
                <Dropdown
                    key="exports-dropdown"
                    defaultValue={""}
                    options={[
                        { text: t("general-export_one"), isAction: true, value: "export", onClick: handleExport },
                        { text: t("general-view-exports"), isAction: true, value: "my-exports", onClick: handleViewExports }
                    ]}
                    placeholder={t("general-export_other")}
                />
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("contacts-listing-search-placeholder")} initValue={search} className={`contactsSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <LoadingContainer loading={!contacts}>
            {contacts && contacts.data.length === 0 && !search &&
                <EmptyContent
                    title={t("contacts-listing-empty-title")}
                    desc={t("contacts-listing-empty-desc")}
                    iconName="CreateContacts44"
                    button={<Stack direction="row" gap={4}>
                        <Button
                            variant="contained"
                            size="medium"
                            color="secondary"
                            onClick={handleImportContacts} iconOnly>
                            {t("general-import-contact_other")}
                        </Button>
                        <Button
                            variant="outlined"
                            size="medium"
                            color="secondary"
                            onClick={handleCreateContact} iconOnly>
                            {t("general-add-contact_one")}
                        </Button>
                    </Stack>}
                />
            }

            {contacts && contacts.data.length === 0 && search &&
                <EmptyContent
                    title={t("contacts-listing-empty-search-title")}
                    desc={t("search-empty-desc")}
                />
            }

            {contacts && contacts.data.length > 0 &&
                <DataTable
                    controlledState={{
                        rowsPerPage: perPage,
                        page: page - 1, // Start at 0
                        order,
                        orderBy,
                        selected: selectedContacts || []
                    }}
                    totalCount={contacts?.pagination?.count}
                    className={`contactsDataTable ${loading ? "loading" : ""}`}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    onPaginationChange={handlePaginationChange}
                    onRowSelect={handleRowSelect}
                    showRowSelection
                    stickyLastCol
                />
            }

            {selectedContacts && selectedContacts.length > 0 &&
                <ContactActionBar
                    selectedContacts={selectedContacts}
                    contacts={contacts}
                    selectAllPages={selectAllPages}
                    handlePageSelection={handlePageSelection}
                    handleToggleInterestManager={handleToggleInterestManager}
                    handleUnsubscribeContacts={handleUnsubscribeContacts}
                    handleSuppressContacts={handleSuppressContacts}
                    handleDeleteContacts={handleDeleteContacts}
                    handleCloseActionRow={handleCloseActionRow}
                />
            }
        </LoadingContainer>
    </Stack>
}